import React, { Component } from 'react';
import { circlePolyline } from '../../util/maps';
import { createLandmarkOverlay } from './GoogleMapLandmarks';

/**
 * Map that uses Google Maps and is fully dynamic (zoom, pan, etc.).
 *
 * @component
 * @param {Object} props
 * @param {string?} props.containerClassName add style rules for the root container
 * @param {string?} props.mapClassName add style rules for the map div
 * @param {string?} props.address
 * @param {Object} props.center LatLng
 * @param {number} props.center.lat latitude
 * @param {number} props.center.lng longitude
 * @param {number} props.zoom
 * @param {Object} props.mapsConfig
 * @returns {JSX.Element} dynamic version of Google Maps
 */
class DynamicGoogleMap extends Component {
  constructor(props) {
    super(props);
    this.map = null;
    this.mapContainer = null;

    this.initializeMap = this.initializeMap.bind(this);
  }

  componentDidMount(prevProps) {
    if (!this.map && this.mapContainer) {
      this.initializeMap();
    }
  }

  componentDidUpdate(prevProps) {
    if (this.map && prevProps.landmarks !== this.props.landmarks) this.updateLandmarks();
  }

  componentWillUnmount() {
    this.landmarkOverlay?.setMap(null);
    this.circle?.setMap(null);
    this.marker?.setMap(null);
  }

  updateLandmarks() {
    this.landmarkOverlay?.setMap(null);
    this.landmarkOverlay = null;
    if (this.props.mapsConfig.fuzzy.enabled && this.props.landmarks?.length) {
      this.landmarkOverlay = createLandmarkOverlay(this.map, this.props.landmarks);
    }
  }

  initializeMap() {
    const { offsetHeight, offsetWidth } = this.mapContainer;
    const hasDimensions = offsetHeight > 0 && offsetWidth > 0;

    if (hasDimensions) {
      const { center, zoom, address, mapsConfig } = this.props;
      const maps = window.google.maps;
      const controlPosition = window.google.maps.ControlPosition.LEFT_TOP;

      const mapConfig = {
        center,
        zoom,
        // Disable all controls except zoom
        // https://developers.google.com/maps/documentation/javascript/reference/map#MapOptions

        // Disable map type (ie. Satellite etc.)
        mapTypeControl: false,
        // Disable zooming by scrolling
        scrollwheel: false,
        // Fullscreen control toggle
        fullscreenControl: false,
        // Street View control
        streetViewControl: false,
        // An approximate pin alone does not conceal a venue named on the base map.
        // Nearby landmarks are drawn separately after excluding the saved venue.
        ...(mapsConfig.fuzzy.enabled
          ? {
              renderingType: 'RASTER', // JSON styles are supported on raster maps.
              styles: [
                {
                  featureType: 'poi',
                  elementType: 'labels',
                  stylers: [{ visibility: 'off' }],
                },
              ],
              clickableIcons: false,
            }
          : {}),
        // Zoom control position
        zoomControlOptions: {
          position: controlPosition,
        },
      };

      this.map = new maps.Map(this.mapContainer, mapConfig);

      if (mapsConfig.fuzzy.enabled) {
        const GoogleLatLng = window.google.maps.LatLng;
        // Origin as object literal (LatLngLiteral)
        const origin = { lat: center.lat, lng: center.lng };
        const radius = mapsConfig.fuzzy.offset;
        const path = circlePolyline(origin, radius).map(c => new GoogleLatLng(c[0], c[1]));

        const circleProps = {
          options: {
            fillColor: mapsConfig.fuzzy.circleColor,
            fillOpacity: 0.2,
            strokeColor: mapsConfig.fuzzy.circleColor,
            strokeOpacity: 0.5,
            strokeWeight: 1,
            clickable: false,
          },
          path,
          map: this.map,
        };

        // Add a circle. We use Polygon because the default Circle class is not round enough.
        const Polygon = window.google.maps.Polygon;
        this.circle = new Polygon(circleProps);
        this.updateLandmarks();
      } else {
        this.marker = new window.google.maps.Marker({
          position: center,
          map: this.map,
          title: address,
        });
      }
    }
  }

  render() {
    const { containerClassName, mapClassName } = this.props;
    return (
      <div className={containerClassName}>
        <div className={mapClassName} ref={el => (this.mapContainer = el)} />
      </div>
    );
  }
}

export default DynamicGoogleMap;
